import { useState } from 'react';
import { compact } from 'lodash';
import { useCurrentStateAndParams, useRouter } from '@uirouter/react';
import { AlertTriangle } from 'lucide-react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useAuthorizations } from '@/react/hooks/useUser';
import { EnvironmentId } from '@/react/portainer/environments/types';
import { useEvents } from '@/react/kubernetes/queries/useEvents';

import { NavTabs, Option } from '@@/NavTabs';
import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { TextTip } from '@@/Tip/TextTip';

import { HelmRelease } from '../../types';
import { useHelmHistory } from '../../helmReleaseQueries/useHelmHistory';
import { useMergedValuesFromStackID } from '../useMergedValuesFromStackID';
import { SyncWithGitButton } from '../SyncWithGitButton';
import { ObservabilityLogs } from '../../../namespaces/components/Observability/ObservabilityLogs';

import { ManifestDetails } from './ManifestDetails';
import { NotesDetails } from './NotesDetails';
import { ValuesDetails } from './ValuesDetails';
import { ResourcesTable } from './ResourcesTable/ResourcesTable';
import { DiffControl, DiffViewMode } from './DiffControl';
import { useHelmReleaseToCompare } from './useHelmReleaseToCompare';
import {
  filterRelatedEvents,
  HelmEventsDatatable,
  useHelmEventsTableState,
} from './HelmEventsDatatable';

type Props = {
  release: HelmRelease;
  selectedRevision: number;
  latestRevision?: number;
};

type Tab = 'values' | 'notes' | 'manifest' | 'resources' | 'events' | 'logs';

export function ReleaseTabs({
  release,
  selectedRevision,
  latestRevision,
}: Props) {
  const {
    params: { tab },
  } = useCurrentStateAndParams();
  const router = useRouter();
  const environmentId = useEnvironmentId();
  // Without a namespace the logs query would silently widen to cluster scope,
  // so the Logs tab requires a known namespace.
  const showLogsTab = !!release.namespace;
  // state is here so that the state isn't lost when the tab changes
  const [isUserSupplied, setIsUserSupplied] = useState(true);
  // start with NaN so that the input is empty (see <Input /> for more details)
  const [selectedCompareRevisionNumber, setSelectedCompareRevisionNumber] =
    useState(NaN);
  const [diffViewMode, setDiffViewMode] = useState<DiffViewMode>('view');

  const historyQuery = useHelmHistory(
    environmentId,
    release.name,
    release.namespace ?? ''
  );
  const earliestRevisionNumber =
    historyQuery.data?.[historyQuery.data.length - 1]?.version ??
    release.version ??
    1;
  const latestRevisionNumber =
    historyQuery.data?.[0]?.version ?? release.version ?? 1;
  const { compareRelease, isCompareReleaseLoading, isCompareReleaseError } =
    useHelmReleaseToCompare(
      release,
      earliestRevisionNumber,
      latestRevisionNumber,
      diffViewMode,
      selectedRevision,
      selectedCompareRevisionNumber
    );

  const { autoRefreshRateMS } = useHelmEventsTableState();
  const { data: eventWarningCount } = useEvents(environmentId, {
    namespace: release.namespace ?? '',
    queryOptions: {
      autoRefreshRate: autoRefreshRateMS,
      select: (data) => {
        const relatedEvents = filterRelatedEvents(
          data,
          release.info?.resources ?? []
        );
        return relatedEvents.filter((e) => e.type === 'Warning').length;
      },
    },
  });

  // To compare with git, we need to be able to fetch values from the git repository
  const mergedValuesQuery = useMergedValuesFromStackID(release.stackID);
  // check if the latest user defined values from the release match the latest git values
  const isGitValuesOutOfSync =
    mergedValuesQuery.isSuccess &&
    release.values?.userSuppliedValues !==
      mergedValuesQuery.data?.mergedValues &&
    // only show the out of sync badge if the selected version is the latest version available
    selectedRevision === latestRevision;
  const showGitOption = mergedValuesQuery.isSuccess && !!release.stackID;

  // The server withholds the chart-rendered parts of a release from users who may not
  // read secret data, so those tabs have nothing to show and are left out. Resources,
  // events and logs are unaffected: they describe live objects, not chart output.
  const { authorized: showChartOutput } =
    useAuthorizations('K8sSecretsDetailsR');

  return (
    <NavTabs<Tab>
      onSelect={setTab}
      selectedId={parseValidTab(
        tab,
        !!release.info?.notes,
        showLogsTab,
        showChartOutput
      )}
      type="pills"
      justified
      options={helmTabs(
        release,
        environmentId,
        isUserSupplied,
        setIsUserSupplied,
        earliestRevisionNumber,
        latestRevisionNumber,
        selectedRevision,
        selectedCompareRevisionNumber,
        setSelectedCompareRevisionNumber,
        diffViewMode,
        handleDiffViewChange,
        isCompareReleaseLoading,
        isCompareReleaseError,
        eventWarningCount ?? 0,
        showGitOption,
        isGitValuesOutOfSync,
        showLogsTab,
        showChartOutput,
        compareRelease
      )}
    />
  );

  function handleDiffViewChange(diffViewMode: DiffViewMode) {
    setDiffViewMode(diffViewMode);

    if (latestRevisionNumber === earliestRevisionNumber) {
      return;
    }

    // if the input for compare revision number is NaN, set it to the previous revision number
    if (
      Number.isNaN(selectedCompareRevisionNumber) &&
      diffViewMode === 'specific'
    ) {
      if (selectedRevision > earliestRevisionNumber) {
        setSelectedCompareRevisionNumber(selectedRevision - 1);
        return;
      }
      // it could be useful to compare to the latest revision number if the selected revision number is the earliest revision number
      setSelectedCompareRevisionNumber(latestRevisionNumber);
    }
  }

  function setTab(tab: Tab) {
    router.stateService.go('kubernetes.helm', {
      tab,
    });
  }
}

function helmTabs(
  release: HelmRelease,
  environmentId: EnvironmentId,
  isUserSupplied: boolean,
  setIsUserSupplied: (isUserSupplied: boolean) => void,
  earliestRevisionNumber: number,
  latestRevisionNumber: number,
  selectedRevisionNumber: number,
  compareRevisionNumber: number,
  setCompareRevisionNumber: (compareRevisionNumber: number) => void,
  diffViewMode: DiffViewMode,
  setDiffViewMode: (diffViewMode: DiffViewMode) => void,
  isCompareReleaseLoading: boolean,
  isCompareReleaseError: boolean,
  eventWarningCount: number,
  showGitOption: boolean,
  isGitValuesOutOfSync: boolean,
  showLogsTab: boolean,
  showChartOutput: boolean,
  compareRelease?: HelmRelease
): Option<Tab>[] {
  // as long as the latest revision number is greater than the earliest revision number, there are changes to compare
  const showDiffControl = latestRevisionNumber > earliestRevisionNumber;

  return compact([
    {
      label: 'Resources',
      id: 'resources',
      children: (
        <ResourcesTable
          title={
            <TextTip inline color="blue" className="!text-xs">
              Only resources currently in the cluster will be displayed.
            </TextTip>
          }
        />
      ),
    },
    {
      label: (
        <>
          Events
          {eventWarningCount >= 1 && (
            <Badge type="warnSecondary">
              <Icon icon={AlertTriangle} className="!mr-1" />
              {eventWarningCount}
            </Badge>
          )}
        </>
      ),
      id: 'events',
      children: (
        <HelmEventsDatatable
          namespace={release.namespace ?? ''}
          releaseResources={release.info?.resources ?? []}
          title={
            <TextTip inline color="blue" className="!text-xs">
              Only events for resources currently in the cluster will be
              displayed.
            </TextTip>
          }
        />
      ),
    },
    showChartOutput && {
      label: (
        <>
          Values
          {isGitValuesOutOfSync && release.stackID && (
            <TooltipWithChildren message="Updated values available from Git repository">
              <span>
                <SyncWithGitButton
                  stackID={release.stackID}
                  release={release}
                  dataCy="values-sync-with-git"
                />
              </span>
            </TooltipWithChildren>
          )}
        </>
      ),
      id: 'values',
      children: (
        <ValuesDetails
          values={release.values}
          isUserSupplied={isUserSupplied}
          selectedRevisionNumber={selectedRevisionNumber}
          diffViewMode={diffViewMode}
          compareValues={compareRelease?.values}
          compareRevisionNumberFetched={compareRelease?.version}
          isCompareReleaseLoading={isCompareReleaseLoading}
          isCompareReleaseError={isCompareReleaseError}
          stackID={release.stackID}
          diffControl={
            <DiffControl
              selectedRevisionNumber={selectedRevisionNumber}
              latestRevisionNumber={latestRevisionNumber}
              earliestRevisionNumber={earliestRevisionNumber}
              compareRevisionNumber={compareRevisionNumber}
              setCompareRevisionNumber={setCompareRevisionNumber}
              diffViewMode={diffViewMode}
              setDiffViewMode={setDiffViewMode}
              isUserSupplied={isUserSupplied}
              setIsUserSupplied={setIsUserSupplied}
              showUserSuppliedCheckbox
              showGitOption={showGitOption}
            />
          }
        />
      ),
    },
    showChartOutput && {
      label: 'Manifest',
      id: 'manifest',
      children: (
        <ManifestDetails
          manifest={release.manifest}
          selectedRevisionNumber={selectedRevisionNumber}
          diffViewMode={diffViewMode}
          compareManifest={compareRelease?.manifest}
          compareRevisionNumberFetched={compareRelease?.version}
          isCompareReleaseLoading={isCompareReleaseLoading}
          isCompareReleaseError={isCompareReleaseError}
          stackID={release.stackID}
          release={release}
          diffControl={
            (showDiffControl || showGitOption) && (
              <DiffControl
                selectedRevisionNumber={selectedRevisionNumber}
                latestRevisionNumber={latestRevisionNumber}
                earliestRevisionNumber={earliestRevisionNumber}
                compareRevisionNumber={compareRevisionNumber}
                setCompareRevisionNumber={setCompareRevisionNumber}
                diffViewMode={diffViewMode}
                setDiffViewMode={setDiffViewMode}
                showGitOption={showGitOption}
              />
            )
          }
        />
      ),
    },
    !!release.info?.notes && {
      label: 'Notes',
      id: 'notes',
      children: (
        <NotesDetails
          notes={release.info.notes}
          selectedRevisionNumber={selectedRevisionNumber}
          diffViewMode={diffViewMode}
          compareNotes={compareRelease?.info?.notes}
          compareRevisionNumberFetched={compareRelease?.version}
          isCompareReleaseLoading={isCompareReleaseLoading}
          isCompareReleaseError={isCompareReleaseError}
          diffControl={
            showDiffControl && (
              <DiffControl
                selectedRevisionNumber={selectedRevisionNumber}
                latestRevisionNumber={latestRevisionNumber}
                earliestRevisionNumber={earliestRevisionNumber}
                compareRevisionNumber={compareRevisionNumber}
                setCompareRevisionNumber={setCompareRevisionNumber}
                diffViewMode={diffViewMode}
                setDiffViewMode={setDiffViewMode}
              />
            )
          }
        />
      ),
    },
    showLogsTab && {
      label: 'Logs',
      id: 'logs',
      children: (
        <div className="h-[720px]">
          <ObservabilityLogs
            environmentId={environmentId}
            namespace={release.namespace}
          />
        </div>
      ),
    },
  ]);
}

function parseValidTab(
  tab: string,
  hasNotes: boolean,
  showLogsTab: boolean,
  showChartOutput: boolean
): Tab {
  if (
    (tab === 'values' && showChartOutput) ||
    (tab === 'manifest' && showChartOutput) ||
    (tab === 'notes' && hasNotes) ||
    tab === 'resources' ||
    tab === 'events' ||
    (tab === 'logs' && showLogsTab)
  ) {
    return tab;
  }
  return 'resources';
}
